/* eslint-disable */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sparkles, MapPin, Globe, ArrowRight, Heart, Star } from "lucide-react";
import API from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

/**
 * Small practitioner card used inside the recommendation strips that appear
 * across the wellness ecosystem (Dosha result, AI Coach, Wellness Hub topic
 * pages, Relationships, Homepage spotlight, Profile favorites).
 *
 * Props:
 *   b           — business doc (from /api/marketplace/recommend or /featured)
 *   isFavorite  — bool
 *   onToggleFavorite — async () => void  (optional; when omitted the heart is hidden)
 */
export function PractitionerCard({ b, isFavorite, onToggleFavorite, compact = false }) {
  const { t } = useTranslation();
  const tier = b?.tier || "standard";
  const isPremium = tier === "premium";
  const isFeatured = tier === "featured";
  const ring = isPremium
    ? "ring-1 ring-[#C8A24A]/55 shadow-[0_0_28px_rgba(200,162,74,0.18)]"
    : isFeatured
      ? "ring-1 ring-[#C8A24A]/30"
      : "";
  return (
    <div
      className={`card-surface overflow-hidden group focus-ring flex flex-col relative ${ring}`}
      data-testid={`practitioner-${b.slug}`}
    >
      {onToggleFavorite && (
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleFavorite(); }}
          className={`absolute top-3 right-3 z-10 w-9 h-9 rounded-full grid place-items-center backdrop-blur transition-colors ${
            isFavorite
              ? "bg-[#C8A24A] text-black"
              : "bg-black/55 text-white/85 hover:bg-black/75"
          }`}
          aria-label={isFavorite ? t("marketplace.unfavorite", "Remove from saved") : t("marketplace.favorite", "Save")}
          data-testid={`fav-${b.slug}`}
        >
          <Heart className={`w-4 h-4 ${isFavorite ? "fill-current" : ""}`} />
        </button>
      )}
      <Link to={`/marketplace/business/${b.slug}`} className="flex flex-col h-full" data-testid={`practitioner-link-${b.slug}`}>
        <div className={`overflow-hidden ${compact ? "aspect-[16/10]" : "aspect-[4/3]"}`}>
          {b.image && (
            <img
              src={b.image}
              alt={b.name}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
              loading="lazy"
            />
          )}
          {(isPremium || isFeatured) && (
            <div className="absolute top-3 left-3 chip chip-active inline-flex items-center gap-1 text-[10px]">
              {isPremium ? <Sparkles className="w-3 h-3" /> : <Star className="w-3 h-3" />}
              {b.badge || (isPremium ? t("marketplace.premium", "Premium") : t("marketplace.featured", "Featured"))}
            </div>
          )}
        </div>
        <div className="p-4 flex-1 flex flex-col">
          <div className="eyebrow mb-1">{(b.category || "").replace(/-/g, " ")}</div>
          <div className="font-display text-lg leading-snug max-w-[28ch]">{b.name}</div>
          {b.tagline && (
            <div className="text-sm text-white/70 mt-1.5 leading-relaxed flex-1 line-clamp-2">{b.tagline}</div>
          )}
          <div className="mt-3 flex items-center gap-3 text-xs text-white/55">
            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {b.city}, {b.country}</span>
            {b.languages_offered?.length > 0 && (
              <span className="flex items-center gap-1">
                <Globe className="w-3 h-3" />
                {b.languages_offered.slice(0, 3).map((l) => l.toUpperCase()).join(" · ")}
              </span>
            )}
          </div>
        </div>
      </Link>
    </div>
  );
}

/**
 * Self-contained recommendations strip. Drops cleanly into any page.
 *
 * Props:
 *   topic            — wellness hub topic slug to filter by (optional)
 *   dosha            — "vata" | "pitta" | "kapha" (optional)
 *   category         — business category slug (optional)
 *   limit            — default 3
 *   title            — section title (translated)
 *   eyebrow          — small label above the title
 *   subtitle         — optional description under the title
 *   ctaTo            — link for "View all" CTA; defaults to /marketplace with the filters applied
 *   testid           — custom data-testid (defaults to "practitioner-recs")
 *   hideIfEmpty      — render nothing when there are no results (default true)
 */
export default function PractitionerRecommendations({
  topic,
  dosha,
  category,
  limit = 3,
  title,
  eyebrow,
  subtitle,
  ctaTo,
  testid = "practitioner-recs",
  hideIfEmpty = true,
}) {
  const { t } = useTranslation();
  const { user } = useAuth() || {};
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [favIds, setFavIds] = useState(new Set());

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (topic) params.set("topic", topic);
    if (dosha) params.set("dosha", dosha);
    if (category) params.set("category", category);
    params.set("limit", String(limit));
    API.get(`/marketplace/recommend?${params.toString()}`)
      .then(({ data }) => setItems(data.items || []))
      .finally(() => setLoading(false));
  }, [topic, dosha, category, limit]);

  // Pull favorites once for logged-in users so hearts render correctly
  useEffect(() => {
    if (!user) { setFavIds(new Set()); return; }
    API.get("/marketplace/favorites/ids")
      .then(({ data }) => setFavIds(new Set(data.ids || [])))
      .catch(() => setFavIds(new Set()));
  }, [user]);

  const toggleFav = async (b) => {
    if (!user) {
      toast.info(t("marketplace.login_to_save", "Sign in to save practitioners"));
      return;
    }
    const wasFav = favIds.has(b.business_id);
    // Optimistic UI
    setFavIds((prev) => {
      const next = new Set(prev);
      if (wasFav) next.delete(b.business_id); else next.add(b.business_id);
      return next;
    });
    try {
      if (wasFav) {
        await API.delete(`/marketplace/favorites/${b.business_id}`);
        toast.success(t("marketplace.removed_from_saved", "Removed from saved"));
      } else {
        await API.post(`/marketplace/favorites/${b.business_id}`);
        toast.success(t("marketplace.added_to_saved", "Saved to your practitioners"));
      }
    } catch {
      // revert on error
      setFavIds((prev) => {
        const next = new Set(prev);
        if (wasFav) next.add(b.business_id); else next.delete(b.business_id);
        return next;
      });
      toast.error(t("marketplace.fav_error", "Could not update saved practitioners"));
    }
  };

  if (loading) {
    return (
      <section className="py-8" data-testid={`${testid}-loading`}>
        <div className="app-container">
          {eyebrow && <div className="eyebrow mb-2">{eyebrow}</div>}
          {title && <h2 className="font-display text-3xl md:text-4xl leading-tight mb-4">{title}</h2>}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[0, 1, 2].slice(0, limit).map((i) => (
              <div key={i} className="card-surface overflow-hidden animate-pulse">
                <div className="aspect-[4/3] bg-white/[0.04]" />
                <div className="p-4 space-y-2">
                  <div className="h-3 w-20 bg-white/[0.06] rounded" />
                  <div className="h-5 w-3/4 bg-white/[0.06] rounded" />
                  <div className="h-3 w-full bg-white/[0.04] rounded" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (hideIfEmpty && items.length === 0) return null;

  const fallbackCta = (() => {
    const params = new URLSearchParams();
    if (topic) params.set("topic", topic);
    if (dosha) params.set("dosha", dosha);
    if (category) params.set("category", category);
    const qs = params.toString();
    return qs ? `/marketplace?${qs}` : "/marketplace";
  })();

  return (
    <section className="py-10" data-testid={testid}>
      <div className="app-container">
        <div className="flex items-end justify-between gap-4 mb-5 flex-wrap">
          <div>
            {eyebrow && <div className="eyebrow mb-2">{eyebrow}</div>}
            {title && <h2 className="font-display text-3xl md:text-4xl leading-tight max-w-[26ch]">{title}</h2>}
            {subtitle && <p className="text-white/70 mt-3 max-w-[60ch] leading-relaxed">{subtitle}</p>}
          </div>
          <Link
            to={ctaTo || fallbackCta}
            className="btn-ghost text-sm inline-flex items-center gap-2 self-end"
            data-testid={`${testid}-view-all`}
          >
            {t("marketplace.view_all", "View all")} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        {items.length === 0 ? (
          <div className="card-surface p-6 text-white/65 text-sm" data-testid={`${testid}-empty`}>
            {t("marketplace.no_recs_yet", "Practitioner recommendations will appear here as more local pros join the directory.")}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={`${testid}-grid`}>
            {items.map((b) => (
              <PractitionerCard
                key={b.business_id}
                b={b}
                isFavorite={favIds.has(b.business_id)}
                onToggleFavorite={() => toggleFav(b)}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
